import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import "./MarkSubmissionConfirmModal.css";
import { useCenter } from "../../context/CenterContext";
import { useSubject } from "../../context/SubjectContext";
import { useUnits } from "../../context/UnitsContext";
import {
  MARK_STATUS,
  AssessmentCriteriaInputValues,
} from "../../constants/Constants";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";
import { useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import { useApi } from "../../context/ApiClient";
import {
  getCenterType,
  checkunitIsConsortiaUnit,
} from "../../utils/utils";

const MarkSubmissionConfirmModal = ({
  isOpen,
  onClose,
  rows,
  setRows,
  candidates,
  anyRowInvalid,
  showError,
  isChecked,
  setIsChecked,
  isRowComplete,
  type,
  assessmentCandidateData,
  isStage3 = false,
  isPostStage3 = false,
  submitACCUpdateMarks,
}) => {
  const modalRef = useRef(null);
  const { subject, unit } = useParams();
  const { subjectData } = useSubject();
  const subjectId = subjectData?.selectedSubjectId;
  const { centerNumber } = useCenter();
  const navigate = useNavigate();
  const location = useLocation();
  const { consortiaUnits } = useUnits();
  const { sessionId } = location.state || {};
  const unitCode = location.state.unit.split(" ")[0];

  const { consortiaCentres = [] } = useConsortiaCenter();
  const currentCentreType =
    getCenterType(consortiaCentres, Number(centerNumber), subjectData?.selectedSubject, subjectData?.selectedSubjectId) || "";
  const consortiaUnitcode =
    String(unit ?? "")
      .toUpperCase()
      .match(/^[A-Z0-9]+/)?.[0] || "";
  const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    consortiaUnitcode,
  );

  const baseUrl = process.env.REACT_APP_AMS_BASE_URL;
  const api = useApi((currentCentreType === 'Lead' && isConsortiaUnit && type === "acmarks") ? `${baseUrl}consortia/` : baseUrl);

  const [inReviewMode, setInReviewMode] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsChecked(false);
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);
  const onClickCancel = () => {
    setIsChecked(false);
    onClose();
  };

  const handlePrimaryClick = () => {
    if (!inReviewMode && type === "marks" && (isStage3 || isPostStage3)) {
      navigate(`/dashboard/marks_submissions_update/${subject}/${unit}`, {
        state: { unit: location.state?.unit || unit, sessionId, rows , isStage3, isPostStage3},
      });
      return;
    }

    handleSubmitMarks();
  };

  const getButtonLabel = () => {
    if ((isStage3 || isPostStage3) && type === "marks") {
      return "Review marks";
    }
    return "Submit marks";
  };
  //  assignmentId for every row
  const transformRows = (rows) => {
    const unitCode = location.state.unit.split(" ")[0];
    return rows.map((row) => {
      if (row.markAsAbsent) {
        return {
          candidateNumber: row.candidateNumber, // add candidate Name if required
          markAsAbsent: true,
          totalCriteriaAchieved: null,
          assignmentId: null,
          markStatusId:
            (!row.markStatus || row.markStatus === "Saved") &&
              (row.isSampled === false || !row.isSampled)
              ? MARK_STATUS.SUBMITTED
              : row.markStatus === "Saved" && row.isSampled === true
                ? MARK_STATUS.RESUBMITTED
                : null,
          centreNumber: Number(
            currentCentreType === "Lead" && isConsortiaUnit
              ? row.centreNumber || centerNumber
              : centerNumber,
          ),
          sessionId: sessionId,
          subjectId: subjectId,
          assessmentCode: unitCode,
          insertedDate: "2025-09-09T19:22:52.726",
          insertedBy: "test",
        };
      }

      return {
        candidateNumber: row.candidateNumber,
        markAsAbsent: false,
        totalCriteriaAchieved: Number(row.totalCriteriaAchieved),
        assignmentId: row.assignmentId || null,
        markStatusId:
          (!row.markStatus || row.markStatus === "Saved") &&
            (row.isSampled === false || !row.isSampled)
            ? MARK_STATUS.SUBMITTED
            : row.markStatus === "Saved" && row.isSampled === true
              ? MARK_STATUS.RESUBMITTED
              : null,
        centreNumber: Number(
          currentCentreType === "Lead" && isConsortiaUnit ? row.centreNumber || centerNumber : centerNumber,
        ),
        sessionId: sessionId,
        subjectId: subjectId,
        assessmentCode: unitCode,
        insertedDate: "2025-09-09T19:22:52.726",
        insertedBy: "test",
      };
    });
  };

  const submitACCMarks = async () => {
    try {
      if (!centerNumber || !rows.length) {
        alert("No data available to submit");
        return;
      }

      // Transform candidatePayloadData → API format
      const formattedData = [];

      rows.forEach((candidate) => {
        const candidateNumber = candidate.centreCandidateNumber;
        const centreNumber = candidate.centreNumber;
        const assessmentCode = candidate.assessmentCode;

        const sessionId = candidate.sessionId;
        candidate.criteria.forEach((c) => {
          let achievedId;
          if (c.achieved === AssessmentCriteriaInputValues.YES) achievedId = 14;
          else if (c.achieved === AssessmentCriteriaInputValues.NO)
            achievedId = 15;
          else if (c.achieved === AssessmentCriteriaInputValues.NOT_ATTEMPTED)
            achievedId = 13;
          else achievedId = null;

          // Find description from assessmentCandidateData
          const candidateMeta = assessmentCandidateData.find(
            (x) => x.centreCandidateNumber === candidate.centreCandidateNumber,
          );
          const criteriaMeta = candidateMeta?.criteriaDetails?.find(
            (cd) => cd.assessmentCriteriaName === c.criteriaName,
          );

          formattedData.push({
            centreNumber,
            sessionId,
            subjectId: subjectId, // replace with real subjectId if available
            assessmentCode,
            candidateNumber,
            markStatusId: MARK_STATUS.SUBMITTED, // "Submitted"
            assessmentCriteriaName: c.criteriaName,
            description:
              criteriaMeta?.assessmentCriteriaDescription || "(No description)",
            assessmentCriteriaAchievedId: achievedId,
            evidenceLocation: c.notes || "",
          });
        });
      });
      let response;

      if (currentCentreType === "Lead") {
        if (isConsortiaUnit === true) {
          // Lead + consortia unit → submit to consortia API
          response = await api.post("submitACMarksConsortia", formattedData);
           if (response.status === 200) {
        toast.success("Your assessment criteria marks have been submitted", {
          style: {
            minWidth: "500px",
          },
        });
        navigate(`/dashboard/marks_submission/${subject}`, {
          state: { mode: "criteria" },
        });

        localStorage.removeItem(`acMarksData_${sessionId}_${centerNumber}`);
        return ;
        }
      }

      }

      response = await api.post("submitAssessmentCriteriaMarks", formattedData);



      if (response.status === 200) {
        toast.success("Your assessment criteria marks have been submitted", {
          style: {
            minWidth: "500px",
          },
        });
        navigate(`/dashboard/marks_submission/${subject}`, {
          state: { mode: "criteria" },
        });

        localStorage.removeItem(`acMarksData_${sessionId}_${centerNumber}`);
      } else {
        alert("Submission failed. Please try again.");
        console.error("Submit failed:", response.data);
      }
    } catch (error) {
      console.error("Error submitting assessment criteria marks:", error);
    }
  };



  const submitMarks = async () => {
    const payload = transformRows(rows);



    if (isConsortiaUnit === undefined || isConsortiaUnit === null) {
      return;
    }
    try {
      const submitEndpoint =
        currentCentreType === "Lead" && isConsortiaUnit
          ? "consortia/submitCandidateMarks"
          : "submitCandidateMarks";

      const response = await api.post(submitEndpoint, payload);

      if (response.status === 200) {
        toast.success("Your total marks have been submitted", {
          style: {
            minWidth: "400px",
          },
        });
        navigate(`/dashboard/marks_submission/${subject}`);
      } else {
        console.error("Failed to submit marks", response);
      }
    } catch (error) {
      console.error("Error submitting marks", error);
    } finally {
      onClose();
    }
  };

  const handleSubmitMarks = () => {
    setIsChecked(false);
    if (type) {
      if (type.toLowerCase() === "acmarks") {
        submitACCMarks()
        return;
      };
      if (type.toLowerCase() === "stage3acmarks") {
        submitACCUpdateMarks();
        return;
      }
      else {
        submitMarks();
        return;
      }
    }

  };
  const onClickConfirm = () => {
    setIsChecked((prev) => !prev);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="modal is-active mark-submit-confirm-modal"
      id="modal-submit-marks-confirmation"
    >
      <div className="modal-background" onClick={onClose}></div>
      <div className="modal-card" ref={modalRef}>
        <header className="modal-card-head">
          <p className="modal-card-title">
            {type === "marks"
              ? `${isStage3 ? "Update" : "Submit"} total marks for ${unit.split("_")[0].toUpperCase()}`
              : `Submit assessment criteria marks for ${unit
                .split("_")[0]
                .toUpperCase()}`}
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={onClose}
          ></button>
        </header>
        <section className="modal-card-body">
          <div className="declaration-confirm">
            <div className="box-style" style={{ marginBottom: "1.5rem" }}>
              <div className="content">
                <div className="field">
                  <div className="control">
                    <label className="label checkbox">
                      <input
                        type="checkbox"
                        id="chkSubmitConfirm"
                        onClick={() => onClickConfirm()}
                        value={isChecked}
                      />
                      <span style={{ marginLeft: "0.5rem" }}>
                        I confirm that:
                      </span>
                    </label>
                  </div>
                </div>

                <ul style={{ marginBottom: "16px" }}>
                  <li>
                    We hold signed candidate declarations authenticating the
                    work of all candidates submitted on the claim.
                  </li>
                  <li>
                    All reasonable steps were taken to make sure the work
                    submitted is solely that of the candidates concerned,
                    including steps to mitigate the risk of AI misuse.
                  </li>
                  <li>
                    The marking was carried out according to the marking
                    criteria and AI was not used as the sole form of marking.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="content">
            {isPostStage3 ? (
               <ul className="notification modal-info-msg xis-size-7">
                You will not be able to amend your marks after you
                have submitted them.
              </ul>
            ) : isStage3 ? (
              <ul className="notification modal-info-msg xis-size-7">
                You will receive your sample request shortly after submitting
                your marks. You will not be able to amend your marks after you
                have submitted them.
              </ul>
            ) : type === "Stage3acmarks" ? (
              <span className="notification xis-size-7">
                You will not be able to amend these marks once you have
                submitted them.
              </span>
            ) : (
              <ul className="notification xis-size-7">
                <li>
                  The earliest you will receive your sample request is 15
                  working days before your visit.
                </li>
                <li>
                  If your visit is within the next 15 working days, you will
                  receive your sample request shortly after submitting your
                  marks.
                </li>
                <li>
                  You will not be able to amend your marks after the sample is
                  produced.
                </li>
              </ul>
            )}
          </div>
        </section>
        <footer className="modal-card-foot">
          <button
            className="button is-primary submit-marks-btn"
            style={{
              backgroundColor: `${isChecked ? "#216D00" : "#757575"}`,
              fontFamily: `'Open Sans', 'Source Sans 3', 'Arial' ,'Helvetica', 'sans-serif' !important`,
            }}
            disabled={!isChecked}
            onClick={handlePrimaryClick}
          >
            {getButtonLabel()}
          </button>
          <button className="button button-custom-font" onClick={onClickCancel}>
            Cancel
          </button>
        </footer>
      </div>
    </div>,
    document.body, //makes it a portal
  );
};

export default MarkSubmissionConfirmModal;

MarkSubmissionConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rows: PropTypes.array,
  isChecked: PropTypes.bool,
  setIsChecked: PropTypes.func,
  type: PropTypes.string,
  assessmentCandidateData: PropTypes.object,
};
